import type { GetQuickFilterTextParams } from 'ag-grid-community';
import type { AgGridSecurityDecorationParams } from '../../AgGrid';
import { defaultGetQuickFilterText } from '../utils';
import type { AssetColumnProps } from './asset';
import { resolveSecurityColumnShowDescription } from './blotterSecurityDescriptionPolicy';
import { resolveBlotterAssetIsCurrency } from './resolveBlotterAssetIsCurrency';
import { createGetSecurityIdentifierColumnQuickFilterText } from './securityIdentifiers';

type CurrencyQuickFilterColumnParams = Pick<AssetColumnProps, 'currencies' | 'showDescription'>;

type AssetQuickFilterColumnParams<T extends object> = Pick<AssetColumnProps<T>, 'currencies' | 'showDescription'> & {
  assetTypeField?: string;
};

interface GetDisplayedAssetDescriptionParams<T extends object> {
  symbol: string;
  data: T | undefined;
  context: AgGridSecurityDecorationParams['context'];
  columnParams: AssetQuickFilterColumnParams<T>;
}

function getDisplayedCurrencyDescription(
  symbol: string,
  context: AgGridSecurityDecorationParams['context'],
  columnParams: CurrencyQuickFilterColumnParams
): string | undefined {
  if (columnParams.showDescription === false) {
    return undefined;
  }

  return (columnParams.currencies ?? context.current.currenciesBySymbol)?.get(symbol)?.Description;
}

export function wrapDisplayedCurrencyDescriptionQuickFilter(
  baseGetQuickFilterText: ((params: GetQuickFilterTextParams) => string) | undefined,
  columnParams: CurrencyQuickFilterColumnParams
): (params: GetQuickFilterTextParams) => string {
  const base = baseGetQuickFilterText ?? defaultGetQuickFilterText;

  return params => {
    const baseText = base(params);
    const symbol = params.value;
    if (typeof symbol !== 'string' || symbol === '') {
      return baseText;
    }

    const description = getDisplayedCurrencyDescription(symbol, params.context, columnParams);
    return description && !baseText.includes(description) ? `${baseText} ${description}` : baseText;
  };
}

export function getDisplayedAssetDescription<T extends object>({
  symbol,
  data,
  context,
  columnParams,
}: GetDisplayedAssetDescriptionParams<T>): string | undefined {
  const isCurrency = resolveBlotterAssetIsCurrency({ context, data, value: symbol }, columnParams.assetTypeField);

  if (isCurrency) {
    return getDisplayedCurrencyDescription(symbol, context, columnParams);
  }

  const security = context.current.securitiesBySymbol?.get(symbol);
  const resolvedSecurityShowDescription = resolveSecurityColumnShowDescription(columnParams);
  const shouldShowDescription =
    typeof resolvedSecurityShowDescription === 'function'
      ? resolvedSecurityShowDescription({ context, data, security })
      : resolvedSecurityShowDescription === true;

  return shouldShowDescription ? security?.Description : undefined;
}

export function wrapDisplayedAssetDescriptionQuickFilter<T extends object>(
  baseGetQuickFilterText: ((params: GetQuickFilterTextParams) => string) | undefined,
  columnParams: AssetQuickFilterColumnParams<T>
) {
  const base = baseGetQuickFilterText ?? defaultGetQuickFilterText;

  return (params: GetQuickFilterTextParams): string => {
    const baseText = base(params);
    const symbol = params.value;
    if (typeof symbol !== 'string' || symbol === '') {
      return baseText;
    }

    const description = getDisplayedAssetDescription({
      symbol,
      data: params.data,
      context: params.context,
      columnParams,
    });

    return description && !baseText.includes(description) ? `${baseText} ${description}` : baseText;
  };
}

export function createGetAssetColumnQuickFilterText<T extends object>(
  columnParams: AssetQuickFilterColumnParams<T>,
  includeSedol?: boolean
) {
  const getIdentifierQuickFilterText = createGetSecurityIdentifierColumnQuickFilterText(includeSedol);
  return wrapDisplayedAssetDescriptionQuickFilter(getIdentifierQuickFilterText, columnParams);
}
